/**
 * Responder/reenviar un correo que llegó como adjunto (.eml) cuando no existe
 * un documento del hilo real en Firestore: arma destinatarios, asunto, cita y
 * cabeceras de hilo para abrir un correo nuevo con los mismos datos que
 * tendría una respuesta normal (ver useEmbeddedEmailReply.ts).
 *
 * Puro a propósito: sin stores ni composables, para poder probarlo con datos
 * fijos.
 */
import type { EmailAddress } from '@/types'
import type { ParsedEml } from '@/utils/emlPreview'
import { buildReplySubject, buildForwardSubject } from '@/utils/emailSubject'
import { buildQuotedMessage, formatQuoteAddress, formatQuoteAddressList, formatQuoteDate } from '@/utils/emailQuote'
import type { QuoteLabels } from '@/utils/emailQuote'
import { sanitizeHtml, resolveCidImages, normalizeContentId, referencedContentIds } from '@/utils/email'

export type EmbeddedAction = 'reply' | 'replyAll' | 'forward'

export interface EmbeddedComposeInput {
  to: EmailAddress[]
  cc: EmailAddress[]
  subject: string
  bodyHtml: string
  attachments: File[]
  inReplyTo?: string
  references?: string
}

/** Convierte a `{email, name}`, descarta vacíos/excluidos/repetidos (minúsculas) y conserva el orden. */
function uniq(list: Array<{ name?: string; email: string }>, excluded: string[]): EmailAddress[] {
  const skip = new Set(excluded.map(e => e.toLowerCase()).filter(Boolean))
  const result: EmailAddress[] = []
  for (const item of list) {
    const email = (item.email || '').trim()
    if (!email) continue
    const key = email.toLowerCase()
    if (skip.has(key)) continue
    skip.add(key)
    result.push({ email, name: item.name || email })
  }
  return result
}

/**
 * Misma regla de Outlook que ya usa `loadOriginalEmail` en EmailComposer.vue:
 * Responder va a Reply-To (o al remitente si no hay), Responder a todos suma
 * el Para/CC originales, y la propia dirección nunca aparece en ningún campo.
 */
export function buildEmbeddedRecipients(
  eml: Pick<ParsedEml, 'fromName' | 'fromAddress' | 'replyTo' | 'toAddresses' | 'ccAddresses'>,
  action: EmbeddedAction,
  ownAddress: string
): { to: EmailAddress[]; cc: EmailAddress[] } {
  if (action === 'forward') return { to: [], cc: [] }

  const own = ownAddress.trim().toLowerCase()
  const sender: EmailAddress | null = eml.fromAddress
    ? { email: eml.fromAddress, name: eml.fromName || eml.fromAddress }
    : null
  const replyTargets: EmailAddress[] = eml.replyTo.length ? eml.replyTo : (sender ? [sender] : [])
  const isOwn = !!own && eml.fromAddress.toLowerCase() === own

  let to: EmailAddress[]
  let cc: EmailAddress[] = []

  if (action === 'reply') {
    to = isOwn ? uniq(eml.toAddresses, [own]) : uniq(replyTargets, [own])
  } else {
    if (isOwn) {
      to = uniq(eml.toAddresses, [own])
    } else {
      to = uniq([...replyTargets, ...eml.toAddresses], [own])
    }
    cc = uniq(eml.ccAddresses, [own, ...to.map(r => r.email)])
  }

  // Respaldo: si no quedó ningún destinatario, el remitente (salvo que sea uno mismo).
  if (to.length === 0 && sender && sender.email.toLowerCase() !== own) {
    to = [sender]
  }

  return { to, cc }
}

const HEADER_TOKEN_RE = /<([^<>\s]+)>/g
const PLAIN_MESSAGE_ID_RE = /^[^\s<>]+@[^\s<>]+$/
/** RFC 5322 limita la línea de cabecera a 998 octetos; un solo token no debería acercarse a eso. */
const MAX_TOKEN_CONTENT_LENGTH = 250
/** ASCII imprimible (0x21-0x7e): nada de control, CR/LF ni UTF-8 crudo en una cabecera. */
const ASCII_PRINTABLE_RE = /^[\x21-\x7e]+$/

/** true si el contenido entre `<` y `>` es corto y ASCII imprimible. */
function isValidTokenContent(inner: string): boolean {
  return inner.length > 0 && inner.length <= MAX_TOKEN_CONTENT_LENGTH && ASCII_PRINTABLE_RE.test(inner)
}

/**
 * `In-Reply-To`/`References` a partir del Message-ID y References crudos del
 * embebido. Nunca deja pasar texto libre, CR/LF, tokens larguísimos ni UTF-8
 * crudo: si no se puede identificar un Message-ID válido, no viaja ninguna
 * cabecera.
 */
export function buildThreadHeaders(
  messageId: string | null,
  references: string | null
): { inReplyTo?: string; references?: string } {
  let mid: string | undefined
  if (messageId) {
    const match = messageId.match(/<([^<>\s]+)>/)
    if (match) {
      if (isValidTokenContent(match[1])) mid = `<${match[1]}>`
    } else if (PLAIN_MESSAGE_ID_RE.test(messageId) && isValidTokenContent(messageId)) {
      mid = `<${messageId}>`
    }
  }
  if (!mid) return {}

  const refTokens = references
    ? Array.from(references.matchAll(HEADER_TOKEN_RE))
        .filter(m => isValidTokenContent(m[1]))
        .map(m => `<${m[1]}>`)
    : []
  const seen = new Set<string>()
  const ordered: string[] = []
  for (const token of [...refTokens, mid]) {
    if (seen.has(token)) continue
    seen.add(token)
    ordered.push(token)
  }

  return { inReplyTo: mid, references: ordered.slice(-20).join(' ') }
}

/** Base64 en bloques, para no reventar el stack con `String.fromCharCode(...bytes)` en archivos grandes. */
export function bytesToDataUri(content: ArrayBuffer, mimeType: string): string {
  const bytes = new Uint8Array(content)
  const chunkSize = 0x8000
  let binary = ''
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize))
  }
  return `data:${mimeType || 'application/octet-stream'};base64,${btoa(binary)}`
}

/**
 * Lista blanca de imágenes que se muestran incrustadas en la cita: nada de
 * svg (podría ejecutar en algún visor) y nada que no sea imagen. El backend
 * solo vuelve adjunto inline un `data:image/*` (`DATA_IMG_RE`), así que un PDF
 * u otro tipo dentro de esta conversión se perdía en silencio: quedaba como un
 * `data:application/pdf` gigante metido en el HTML y nunca como adjunto.
 */
const ALLOWED_INLINE_IMAGE_MIME_RE = /^image\/(png|jpeg|gif|webp|bmp)$/i

function isAllowedInlineImage(mimeType: string): boolean {
  return ALLOWED_INLINE_IMAGE_MIME_RE.test((mimeType || '').trim())
}

export function buildEmbeddedComposeInput(
  eml: ParsedEml,
  action: EmbeddedAction,
  opts: { ownAddress: string; locale: string; labels: QuoteLabels }
): EmbeddedComposeInput {
  const { to, cc } = buildEmbeddedRecipients(eml, action, opts.ownAddress)
  const subject = action === 'forward' ? buildForwardSubject(eml.subject, opts.locale) : buildReplySubject(eml.subject)

  // Solo las imágenes de la lista blanca se ven en el editor como data: y el
  // backend las vuelve a convertir en inline al enviar (extractInlineImages).
  // Las que no resuelven (tipo no permitido, o el HTML no las referencia) se
  // quitan de la cita: un embebido no tiene de dónde sacar esas imágenes.
  const allowedInline = eml.inlineImages.filter(i => isAllowedInlineImage(i.mimeType))
  const inline = allowedInline.map(i => ({ contentId: i.contentId, url: bytesToDataUri(i.content, i.mimeType) }))
  const bodyContent = sanitizeHtml(resolveCidImages(eml.html || '', inline))

  const quote = buildQuotedMessage(
    {
      from: formatQuoteAddress({ name: eml.fromName, email: eml.fromAddress }),
      sent: formatQuoteDate(eml.date ? new Date(eml.date) : null, opts.locale),
      to: formatQuoteAddressList(eml.toAddresses),
      cc: formatQuoteAddressList(eml.ccAddresses),
      subject: eml.subject || '',
    },
    opts.labels,
    bodyContent
  )
  const bodyHtml = '<div><br></div>' + quote

  // Outlook no arrastra adjuntos al responder; al reenviar sí, como archivos
  // normales del compositor (nunca hay documento de proveedor que los traiga
  // solo). Además de los adjuntos normales, se suma toda parte inline que no
  // sea una imagen permitida o que el HTML no referencie: si no, se perdía en
  // silencio (por ejemplo, un PDF inline de Apple Mail).
  let attachments: File[] = []
  if (action === 'forward') {
    const referenced = referencedContentIds(eml.html || '')
    const orphanInline = eml.inlineImages.filter(i => {
      if (!isAllowedInlineImage(i.mimeType)) return true
      return !referenced.has(normalizeContentId(i.contentId || ''))
    })
    attachments = [...eml.attachments, ...orphanInline].map(a => new File([a.content], a.filename, { type: a.mimeType }))
  }

  const input: EmbeddedComposeInput = { to, cc, subject, bodyHtml, attachments }
  if (action !== 'forward') {
    Object.assign(input, buildThreadHeaders(eml.messageId, eml.references))
  }
  return input
}
